import { Component, OnInit, OnDestroy } from '@angular/core';
import { Customer, CustomerStoreFacade } from '@modules/customer-store';
import { Router } from '@angular/router';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, take, switchMap, tap, takeUntil } from 'rxjs/operators';
import { RouteNames } from '@nx-ng/constants';
import { B2CEventsService } from '@nx-ng/b2c/b2c-events.service';
import { Actions, ofType } from '@ngrx/effects';
import { CustomerStoreActions } from '@modules/customer-store';
import { parseQueryString } from '@nx-ng/util/parse-query-string';
import { customerHasMissingFields } from '@modules/customer-store/util';

@Component({
  selector: 'nx-ng-ticket-types-page',
  templateUrl: './post-login-page.component.html',
})
export class PostLoginPageComponent implements OnInit, OnDestroy {
  constructor(
    private customerStoreFacade: CustomerStoreFacade,
    private router: Router,
    private b2cEventsService: B2CEventsService,
    private actions$: Actions
  ) {}

  private unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.b2cEventsService.isLoggedIn$
      .pipe(
        tap((isLoggedIn: boolean | undefined) => {
          if (isLoggedIn === true) {
            this.customerStoreFacade.loadCustomer();
          } else if (isLoggedIn === false) {
            this.router.navigate([RouteNames.TICKET_TYPES]);
          }
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(
          CustomerStoreActions.loadDataAccessCustomerDetailsNotFound,
          CustomerStoreActions.loadDataAccessCustomerDetailsFailure
        ),
        take(1)
      )
      .subscribe((action) => {
        this.router.navigate([RouteNames.CUSTOMER_DETAILS]);
      });

    this.actions$
      .pipe(
        ofType(CustomerStoreActions.loadDataAccessCustomerDetailsSuccess),
        take(1),
        switchMap(({ loadedCustomer }: { loadedCustomer: Customer }) =>
          combineLatest([
            this.b2cEventsService.redirectPath$,
            this.b2cEventsService.redirectQueryString$,
            of(loadedCustomer),
          ]).pipe(take(1))
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        ([redirectPath, redirectQueryString, loadedCustomer]: [
          string | undefined,
          string | undefined,
          Customer
        ]) => {
          if (customerHasMissingFields(loadedCustomer)) {
            this.router.navigate([RouteNames.CUSTOMER_DETAILS]);
            return;
          }

          if (!redirectPath) {
            this.router.navigate([RouteNames.TICKET_TYPES]);
            return;
          }
          this.b2cEventsService.clearRedirectPath();

          if (!redirectQueryString) {
            this.router.navigate([redirectPath]);
            return;
          }

          const queryParams = parseQueryString(redirectQueryString);
          this.router.navigate([redirectPath], {
            queryParams: {
              ...queryParams,
            },
          });
        }
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
